import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Col, Form, Row, Spinner } from '@/components/ui';
import ResponsiveModal from '../../../components/ResponsiveModal';
import { useAuth } from '../../../contexts/AuthContext';
import { openWeeklyMaintenanceSchedulePrint } from '../utils/scheduleWeeklyMaintenancePrint';

type ScheduleFilterFields = {
  /** Service-area ids/names selected on the Schedule page (multiselect; empty = all). */
  serviceAreas: string[];
  /** Foreman-area ids selected on the Schedule page (multiselect; empty = all). */
  foremanAreas: string[];
  startDate: string;
  endDate: string;
};

interface PrintScheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  appliedFilters: ScheduleFilterFields;
  serviceAreas: Array<{ id: number; name: string }>;
  foremanAreas: Array<{
    id: number;
    name: string;
    serviceAreaId: number;
    serviceAreaName: string;
  }>;
}

type Step = 'choose' | 'weekly';

function addDaysToIsoDate(isoDate: string, days: number): string {
  if (!isoDate) return '';
  const [yearRaw, monthRaw, dayRaw] = isoDate.split('-');
  const year = Number(yearRaw);
  const month = Number(monthRaw);
  const day = Number(dayRaw);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return '';

  const date = new Date(year, month - 1, day);
  date.setDate(date.getDate() + days);

  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

const PrintScheduleModal: React.FC<PrintScheduleModalProps> = ({
  isOpen,
  onClose,
  appliedFilters,
  serviceAreas,
  foremanAreas,
}) => {
  const { currentUser } = useAuth();
  const [step, setStep] = useState<Step>('choose');
  const [weeklyForeman, setWeeklyForeman] = useState('');
  const [weeklyStart, setWeeklyStart] = useState('');
  const [weeklyEnd, setWeeklyEnd] = useState('');
  const [isWeeklyEndManuallyEdited, setIsWeeklyEndManuallyEdited] = useState(false);
  const [weeklyLoading, setWeeklyLoading] = useState(false);

  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      setStep('choose');
      // The weekly report covers exactly one foreman area, so a multi-selection cannot be carried
      // over as-is; preselect only when the page is filtered to a single area.
      const selectedForemanAreas = Array.isArray(appliedFilters.foremanAreas)
        ? appliedFilters.foremanAreas
        : [];
      setWeeklyForeman(selectedForemanAreas.length === 1 ? selectedForemanAreas[0] : '');
      const startFromSchedule = appliedFilters.startDate || '';
      setWeeklyStart(startFromSchedule);
      /** Weekly maintenance uses a 1-week window from start, not the Schedule page end date (often 2 weeks). */
      setWeeklyEnd(startFromSchedule ? addDaysToIsoDate(startFromSchedule, 7) : '');
      setIsWeeklyEndManuallyEdited(false);
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, appliedFilters]);

  const handleWeeklyStartChange = useCallback((value: string) => {
    setWeeklyStart(value);
    if (!value || isWeeklyEndManuallyEdited) return;
    setWeeklyEnd(addDaysToIsoDate(value, 7));
  }, [isWeeklyEndManuallyEdited]);

  const handleWeeklyEndChange = useCallback((value: string) => {
    setWeeklyEnd(value);
    setIsWeeklyEndManuallyEdited(true);
  }, []);

  /** Joined so the memo below depends on selection *content*, not on a fresh array identity. */
  const appliedServiceAreasKey = (Array.isArray(appliedFilters.serviceAreas)
    ? appliedFilters.serviceAreas
    : []
  ).join(',');

  const foremanOptions = useMemo(() => {
    const list = Array.isArray(foremanAreas) ? foremanAreas : [];
    const selectedServiceAreas = new Set(appliedServiceAreasKey.split(',').filter((s) => s.length > 0));
    let base =
      selectedServiceAreas.size === 0
        ? list
        : list.filter((fa) => selectedServiceAreas.has(String(fa.serviceAreaId)));
    if (weeklyForeman && !base.some((fa) => String(fa.id) === weeklyForeman)) {
      const extra = list.find((fa) => String(fa.id) === weeklyForeman);
      if (extra) base = [extra, ...base];
    }
    return base;
  }, [foremanAreas, appliedServiceAreasKey, weeklyForeman]);

  const handlePrintCurrentSchedule = useCallback(() => {
    const w = window as unknown as { __printSchedulePdf?: () => void };
    if (typeof w.__printSchedulePdf === 'function') {
      w.__printSchedulePdf();
      onClose();
    } else {
      alert('Schedule chart is not ready yet. Try again in a moment.');
    }
  }, [onClose]);

  const handleWeeklyGenerate = useCallback(async () => {
    const fa = weeklyForeman.trim();
    const start = weeklyStart.trim();
    const end = weeklyEnd.trim();

    if (!fa) {
      alert('Please select a foreman area.');
      return;
    }
    if (!start || !end) {
      alert('Please select a start date and an end date.');
      return;
    }

    let dateFrom = start;
    let dateTo = end;
    if (dateFrom > dateTo) {
      const t = dateFrom;
      dateFrom = dateTo;
      dateTo = t;
    }

    const faMeta = foremanOptions.find((x) => String(x.id) === fa);
    if (!faMeta) {
      alert('Could not resolve the selected foreman area.');
      return;
    }

    const foremanAreaText = (faMeta.name || '').trim() || '—';
    const serviceAreaNameResolved =
      (faMeta.serviceAreaName && String(faMeta.serviceAreaName).trim()) ||
      serviceAreas.find((s) => s.id === faMeta.serviceAreaId)?.name ||
      '—';

    const scheduledBy = [currentUser?.name, currentUser?.lastName].filter(Boolean).join(' ').trim() || '—';

    setWeeklyLoading(true);
    try {
      await openWeeklyMaintenanceSchedulePrint({
        /** Scope tasks to this foreman’s service area so the header and API match. */
        serviceArea: String(faMeta.serviceAreaId),
        foremanArea: fa,
        dateFrom,
        dateTo,
        managerAreaName: foremanAreaText,
        serviceAreaName: serviceAreaNameResolved,
        scheduledBy,
      });
      onClose();
    } catch (err) {
      console.error('Failed to generate weekly maintenance schedule:', err);
      alert('Failed to generate weekly maintenance schedule. Please try again.');
    } finally {
      setWeeklyLoading(false);
    }
  }, [
    weeklyForeman,
    weeklyStart,
    weeklyEnd,
    foremanOptions,
    serviceAreas,
    currentUser?.name,
    currentUser?.lastName,
    onClose,
  ]);

  const title = step === 'choose' ? 'Print schedule' : 'Weekly maintenance schedule';

  const footer =
    step === 'choose' ? (
      <>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
      </>
    ) : (
      <>
        <Button variant="outline-secondary" onClick={() => setStep('choose')} disabled={weeklyLoading}>
          Back
        </Button>
        <Button variant="primary" onClick={() => void handleWeeklyGenerate()} disabled={weeklyLoading}>
          {weeklyLoading ? (
            <>
              <Spinner animation="border" size="sm" className="tw-mr-2" />
              Generating…
            </>
          ) : (
            'Generate'
          )}
        </Button>
      </>
    );

  return (
    <ResponsiveModal isOpen={isOpen} onClose={onClose} title={title} size="medium" footer={footer}>
      {step === 'choose' ? (
        <div className="tw-flex tw-flex-col tw-gap-3">
          <p className="tw-mb-0 tw-text-content-muted tw-text-sm">Choose which schedule you want to generate.</p>
          <div className="tw-grid tw-gap-2">
            <Button variant="primary" className="tw-text-left tw-py-3" onClick={handlePrintCurrentSchedule}>
              <span className="tw-font-semibold tw-block">Schedule</span>
              <span className="tw-text-sm tw-opacity-75">Print the current chart (same as before)</span>
            </Button>
            <Button variant="outline-primary" className="tw-text-left tw-py-3" onClick={() => setStep('weekly')}>
              <span className="tw-font-semibold tw-block">Weekly maintenance schedule</span>
              <span className="tw-text-sm tw-text-content-muted">Routine tasks for a foreman area and date range</span>
            </Button>
          </div>
        </div>
      ) : (
        <div className="tw-flex tw-flex-col tw-gap-3">
          <p className="tw-mb-0 tw-text-content-muted tw-text-sm">
            Select a foreman area and date range. Values default from your applied filters when set; otherwise choose
            them here.
          </p>
          <Form.Group>
            <Form.Label className="tw-text-sm">Foreman area</Form.Label>
            <Form.Select
              value={weeklyForeman}
              onChange={(e) => setWeeklyForeman(e.target.value)}
              disabled={weeklyLoading}
            >
              <option value="">Select foreman area…</option>
              {foremanOptions.map((fa) => (
                <option key={fa.id} value={String(fa.id)}>
                  {fa.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Row>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="tw-text-sm">Start date</Form.Label>
                <Form.Control
                  type="date"
                  value={weeklyStart}
                  onChange={(e) => handleWeeklyStartChange(e.target.value)}
                  disabled={weeklyLoading}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="tw-text-sm">End date</Form.Label>
                <Form.Control
                  type="date"
                  value={weeklyEnd}
                  onChange={(e) => handleWeeklyEndChange(e.target.value)}
                  disabled={weeklyLoading}
                />
              </Form.Group>
            </Col>
          </Row>
        </div>
      )}
    </ResponsiveModal>
  );
};

export default PrintScheduleModal;
